import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import { STATSIG_EXPERIMENTS_CONFIG, STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import Image from '@src/quince-site-common/src/domains/Image'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductImage from '@src/quince-site-common/src/domains/ProductImage'
import ProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { getColorFromSelectedOptions } from '@src/quince-site-common/src/helpers/productHelper'

export const VIDEO_FORCE_POSITON = 2

export const getColorProductMediaCombinations = (product: Product) => {
  const colorOption = product.productOptions.find(po => po.name === ProductOptionName.Color)
  if (!colorOption?.values) {
    return {}
  }
  const colorValueProductImagesMap: Record<string, ProductMedia[]> = {}

  for (const color of colorOption.values) {
    const colorOptions = [
      new ProductVariantOption({
        name: ProductOptionName.Color,
        value: color.value,
      }),
    ]

    const productImages = productHelper.getImagesForOptions(product, colorOptions)

    const productVideo = productHelper.getVideoForOptions(product, colorOptions)[0]

    const productMedia: ProductMedia[] = [...productImages]

    if (productVideo) {
      const forcedVideoIndex = Math.min(VIDEO_FORCE_POSITON - 1, productMedia.length)

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const videoIndex = forcedVideoIndex ?? productVideo.videoIndex ?? productImages.length

      productMedia.splice(videoIndex, 0, productVideo)
    }

    colorValueProductImagesMap[color.value] = updatePrimaryMediaForSEOCompliance(
      productMedia,
      color.value,
      product.title,
    )
  }
  return colorValueProductImagesMap
}

const getMediaWithSEOAltText = (mediaItem: ProductMedia, productColor: string, productTitle: string) => {
  return new ProductImage({
    ...mediaItem,
    image: new Image({
      ...(mediaItem as ProductImage).image,
      alt: `${productColor} ${productTitle}`,
    }),
  })
}

export const updatePrimaryMediaForSEOCompliance = (
  productMedia: ProductMedia[],
  productColor: string,
  productTitle: string,
) => {
  const mediaToBeUpdated = productMedia[0]
  if (mediaToBeUpdated && 'image' in mediaToBeUpdated && productColor && productTitle) {
    const updatedMediaItem = getMediaWithSEOAltText(mediaToBeUpdated, productColor, productTitle)
    return [updatedMediaItem, ...productMedia.slice(1)].filter(Boolean)
  }
  return productMedia
}

export const getProductMedia = (product: Product, selectedOptions: ProductVariantOption[]) => {
  const { title } = product
  const color = getColorFromSelectedOptions(selectedOptions)
  const productMedia = productHelper.getProductMedia(product, selectedOptions, {
    forcedVideoPosition: VIDEO_FORCE_POSITON,
  })
  return updatePrimaryMediaForSEOCompliance(productMedia, color, title)
}

export interface FilteredTagsResult {
  applicableTags: ProductCardTag[]
}

export const filterApplicableTags = (
  applicableTags: ProductCardTag[],
  urgencyTagsBucket: string | null | undefined,
): FilteredTagsResult => {
  const showDefaultTags =
    urgencyTagsBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.URGENCY_TAGS].buckets?.groupB

  const filteredTags = applicableTags
    .filter((item: ProductCardTag) => {
      switch (item.value) {
        // hardcoded for now to hide valentine tag
        case 'GET IT BY 02/14':
          return false
        case 'Today Only':
          return false
        case 'Best seller':
          return showDefaultTags
        case 'Low Stock':
          return showDefaultTags
        default:
          return true
      }
    })
    .filter((item: ProductCardTag) => !item.value.includes('Earn $'))

  return {
    applicableTags: filteredTags,
  }
}
